import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useCreateInquiry } from '@/api/contact/useCreateInquiry';
import LogoIcon from '@/component-library/primitives/Icons/LogoIcon/LogoIcon';
import { ProvideToasts, Toast, ToastType } from '@/component-library/primitives/Toast/Toast';
import { CookieSettingsLink } from '@/modules/Pages/shared/CookieConsent';
import { CreateInquiryRequest } from '@/models/Inquiry';
import { AppRoutes } from '@/utils/routes/router';

export default function ContactPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    reason: '',
    message: '',
  });
  const createInquiry = useCreateInquiry();
  const [open, setOpen] = useState(false);
  const [toastInfo, setToastInfo] = useState({ message: '', toastType: '' });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const inquiryData: CreateInquiryRequest = {
      firstName: '', // Default empty values for removed fields
      lastName: '',
      email: formData.email.trim(),
      reason: 'Question' as 'Registration' | 'Demo' | 'Question', // Default to 'Question'
      text: formData.message,
    };

    createInquiry.mutate(inquiryData, {
      onSuccess: () => {
        setToastInfo({ message: 'Message sent successfully!', toastType: 'success' });
        setOpen(true);

        // Reset form completely
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          reason: '',
          message: '',
        });

        // Clear any active focus styles by blurring the active element
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }

        // Reset any input border colors back to default
        const inputs = document.querySelectorAll('input, textarea, select');
        inputs.forEach((input) => {
          if (input instanceof HTMLElement) {
            input.style.borderColor = 'rgba(250, 250, 250, 0.1)';
          }
        });
      },
      onError: () => {
        setToastInfo({ message: 'Failed to send message. Please try again.', toastType: 'error' });
        setOpen(true);
      },
    });
  };

  const inputStyles = {
    backgroundColor: 'rgba(250, 250, 250, 0.05)',
    borderColor: 'rgba(250, 250, 250, 0.1)',
    color: '#fafafa',
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    e.target.style.borderColor = '#00b9ff';
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    e.target.style.borderColor = 'rgba(250, 250, 250, 0.1)';
  };

  return (
    <div className="min-h-screen text-white font-sans overflow-hidden relative" style={{ backgroundColor: '#0e1c2a' }}>
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 w-full z-[9999] transition-all duration-300 ease-in-out`}
        style={{
          backgroundColor: isScrolled ? 'rgba(14, 28, 42, 0.95)' : 'rgba(14, 28, 42, 1)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: isScrolled ? '1px solid rgba(250, 250, 250, 0.08)' : '1px solid rgba(250, 250, 250, 0.05)',
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.4)',
        }}
      >
        <div className="max-w-6xl mx-auto py-6">
          <div className="flex justify-between items-center">
            <div
              style={{
                transition: 'all 0.3s ease',
                width: isScrolled ? '100px' : '120px',
                height: isScrolled ? '36px' : '36px',
              }}
            >
              <LogoIcon withText wordmarkColor="#FAFAFA" />
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-24 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight pt-6" style={{ color: '#fafafa' }}>
              Get in Touch
            </h1>
            <p className="text-xl leading-relaxed max-w-2xl mx-auto" style={{ color: '#999999' }}>
              Ready to reduce churn and boost retention? Let us discuss how ChurnSignal can transform your business.
            </p>
          </div>

          {/* Content Grid */}
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Form Section */}
            <div className="order-2 lg:order-1">
              <div
                className="p-8 rounded-2xl border"
                style={{
                  backgroundColor: 'rgba(250, 250, 250, 0.02)',
                  borderColor: 'rgba(250, 250, 250, 0.08)',
                }}
              >
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Email Field */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#fafafa' }}>
                      Email
                    </label>
                    <input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border transition-colors duration-200 focus:outline-none"
                      style={inputStyles}
                      placeholder="Enter your email"
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                  </div>

                  {/* Message Field */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#fafafa' }}>
                      Message <span style={{ color: '#999999' }}>(optional)</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={5}
                      className="w-full px-4 py-3 rounded-lg border transition-colors duration-200 focus:outline-none resize-none"
                      style={inputStyles}
                      placeholder="Tell us more about your needs..."
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={createInquiry.isLoading}
                    className="w-full px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{
                      backgroundColor: createInquiry.isLoading ? '#0099cc' : '#00b9ff',
                      color: '#0a0a0a',
                      boxShadow: '0 4px 20px rgba(0, 185, 255, 0.3)',
                    }}
                  >
                    {createInquiry.isLoading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>

            {/* Info Section */}
            <div className="order-1 lg:order-2">
              {/* Contact Info Card */}
              <div
                className="p-8 rounded-2xl border"
                style={{
                  backgroundColor: 'rgba(250, 250, 250, 0.02)',
                  borderColor: 'rgba(250, 250, 250, 0.08)',
                }}
              >
                <h3 className="text-xl font-semibold mb-6" style={{ color: '#fafafa' }}>
                  Contact Information
                </h3>
                <div className="space-y-3">
                  <p style={{ color: '#cccccc' }}>Blackfield Labs doo</p>
                  <p style={{ color: '#cccccc' }}>Petra Drapsina 36</p>
                  <p style={{ color: '#cccccc' }}>21000 Novi Sad</p>
                  <a
                    href="mailto:nenad.stojkovic@blackfieldlabs.com"
                    className="inline-block transition-colors duration-200 hover:text-white"
                    style={{ color: '#00b9ff' }}
                  >
                    nenad.stojkovic@blackfieldlabs.com
                  </a>
                </div>

                <div className="flex items-center gap-4 mt-8">
                  <a
                    href="https://www.linkedin.com/company/churnsignal/"
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 rounded-lg transition-all duration-200 hover:scale-110"
                    style={{
                      backgroundColor: 'rgba(0, 185, 255, 0.1)',
                      color: '#00b9ff',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(0, 185, 255, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(0, 185, 255, 0.1)';
                    }}
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="text-center py-8 mt-16">
        <p style={{ color: '#666666' }}>
          © 2026 ChurnSignal. All rights reserved.{' '}
          <Link to={AppRoutes.cookies} style={{ color: '#00b9ff' }}>
            Cookies
          </Link>
          {' · '}
          <CookieSettingsLink className="underline-offset-2 hover:underline" />
        </p>
      </footer>

      {/* Toast with custom positioning to avoid header overlap */}
      <div className="fixed top-24 right-4 z-[10000]">
        <ProvideToasts>
          <Toast
            isOpen={open}
            setOpen={setOpen}
            message={toastInfo.message}
            toastType={toastInfo.toastType as ToastType}
          />
        </ProvideToasts>
      </div>
    </div>
  );
}
